<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="container-fluid px-0">
        <div class="row mb-3 g-2 px-4 pt-4 align-items-end">
         
          <div class="col-12 ">
            <label class="form-label text-xs text-muted mb-1">{{ $t('teacherLessons.teacher') }}</label>
            <select
              class="form-select form-select-sm"
              v-model="teacherId"
              :disabled="isLoadingTeachers"
            >
              <option value="">{{ $t('teacherLessons.chooseTeacher') }}</option>
              <option v-for="t in teachers" :key="t.id" :value="t.id">
                {{ t.username }}
              </option>
            </select>
            <small v-if="!isLoadingTeachers && teachers.length === 0" class="text-danger">
              {{ $t('teacherLessons.noEligibleTeachers') }}
            </small>
          </div>
        </div>

        <div class="row px-4" v-if="teacherId">
          <div class="col-12">
            <div class="d-flex align-items-center gap-2 p-3 mb-1 rounded" style="background-color: #eaf4fd;">
              <i class="fas fa-info-circle text-info"></i>
              <span class="text-xs text-dark">{{ $t('teacherLessons.infoBanner') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ $t('teacherLessons.recordLessons') }}</h6>
          </div>
        </div>

        <div class="table-responsive p-4">
          <LoadingSpinner v-if="isLoading" :message="$t('common.loading')" />

          <template v-else-if="!teacherId">
            <div class="text-center py-4 text-muted">
              <i class="fas fa-user-check fa-2x mb-2"></i>
              <p class="text-sm">{{ $t('teacherLessons.selectTeacherFirst') }}</p>
            </div>
          </template>

          <template v-else-if="rows.length > 0">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">#</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacherLessons.class') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacherLessons.subject') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teacherLessons.count') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teacherLessons.rowTotal') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in rows" :key="row.assignment_id">
                  <td>
                    <span class="text-xs text-muted">{{ idx + 1 }}</span>
                  </td>
                  <td>
                    <span class="text-xs">{{ row.class_name }} / {{ row.group_name }}</span>
                  </td>
                  <td>
                    <span class="text-xs">{{ row.subject_name }}</span>
                  </td>
                  <td class="text-center">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      class="form-control form-control-sm text-center"
                      style="max-width: 90px; margin: 0 auto;"
                      v-model.number="row.count"
                    />
                  </td>
                  <td class="text-center">
                    <span class="text-xs fw-bold text-success">
                      {{ (row.count * row.lesson_price).toFixed(2) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-link text-danger p-1 mx-1"
                      @click="removeRow(idx)"
                      :title="$t('teacherLessons.removeSubject')"
                    >
                      <i class="fas fa-trash fa-sm"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="row mt-4 px-1 g-3">
              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 rounded" style="background-color: #fdf6ec; border: 1px solid #f3e2c3;">
                  <i class="fas fa-sticky-note text-warning mt-1"></i>
                  <div class="row flex-grow-1 g-2">
                    <div class="col-12 col-md-6">
                      <label class="form-label text-xs text-muted mb-1">{{ $t('teacherLessons.sharedNotes') }}</label>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="sharedNotes"
                        :placeholder="$t('teacherLessons.sharedNotesPlaceholder')"
                      />
                    </div>
                    <div class="col-12 col-md-6">
                      <label class="form-label text-xs text-muted mb-1">
                        {{ $t('teacherLessons.recordDate') }}
                        <i v-if="isRefreshingDate" class="fas fa-spinner fa-spin ms-1 text-muted"></i>
                      </label>
                      <input
                        type="date"
                        class="form-control form-control-sm"
                        v-model="date"
                        :max="new Date().toISOString().split('T')[0]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-3 px-1 g-2">
              <div class="col-6">
                <div class="text-center p-3 rounded" style="background-color: #f8f9fa;">
                  <i class="fas fa-layer-group text-muted mb-1"></i>
                  <div class="text-lg fw-bold text-dark">{{ totalCount }}</div>
                  <div class="text-xs text-muted">{{ $t('teacherLessons.totalLessons') }}</div>
                </div>
              </div>
              <div class="col-6">
                <div class="text-center p-3 rounded" style="background-color: #f8f9fa;">
                  <i class="fas fa-calculator text-primary mb-1"></i>
                  <div class="text-lg fw-bold text-primary">{{ totalDue.toFixed(2) }} {{ $t('common.currency') }}</div>
                  <div class="text-xs text-muted">{{ $t('teacherLessons.totalDue') }}</div>
                </div>
              </div>
            </div>

            <div class="mt-4 d-flex gap-2">
              <button @click="saveLessons" class="btn btn-success btn-sm flex-grow-1" :disabled="isSaving">
                <span v-if="isSaving"><i class="fas fa-spinner fa-spin me-2"></i>{{ $t('common.saving') }}</span>
                <span v-else><i class="fas fa-save ms-2"></i>{{ $t('teacherLessons.saveLessons') }}</span>
              </button>
              <button @click="handleCancel" class="btn btn-outline-secondary btn-sm flex-grow-1" :disabled="isSaving">
                <i class="fas fa-times ms-2"></i>{{ $t('common.cancel') }}
              </button>
            </div>
          </template>

          <template v-else>
            <div class="text-center py-4 text-muted">
              <i class="fas fa-chalkboard fa-2x mb-2"></i>
              <p class="text-sm">{{ $t('teacherLessons.noAssignments') }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const router = useRouter();
const toast = useToast();

const date = ref(new Date().toISOString().split('T')[0]);
const teacherId = ref('');
const teachers = ref([]);
const isLoadingTeachers = ref(false);

const rows = ref([]);
const sharedNotes = ref('');
const isLoading = ref(false);
// Separate from isLoading on purpose: isLoading gates the whole form (see
// the v-if below) and swaps it for a spinner — fine for the heavy "teacher
// changed, reload everything" case, but refreshExistingForDate() used to
// reuse it too, which meant the very <input type="date"> the admin was
// mid-keystroke in got unmounted and rebuilt on every partial date typed,
// losing focus and reading as "the page refreshes when I type the date".
const isRefreshingDate = ref(false);
const isSaving = ref(false);

const totalCount = computed(() => rows.value.reduce((sum, r) => sum + (Number(r.count) || 0), 0));
const totalDue = computed(() => rows.value.reduce((sum, r) => sum + (Number(r.count) || 0) * (Number(r.lesson_price) || 0), 0));

// Every teacher in the school shows up here, regardless of salary type —
// recording a lesson for a fixed-salary-only teacher simply doesn't affect
// their pay (that happens downstream in payroll), but the admin should
// still be able to find and pick any teacher in this filter.
const fetchTeachers = async () => {
  isLoadingTeachers.value = true;
  try {
    const response = await api.get('/teachers', { params: { per_page: 1000 } });
    const items = response.data.data || response.data || [];
    teachers.value = items;
  } catch (error) {
    toast.error(t('teacherLessons.fetchTeachersError'));
  } finally {
    isLoadingTeachers.value = false;
  }
};

// Loads every class/group/subject the selected teacher is assigned to, then
// overlays whatever lessons are already recorded for the chosen date so an
// admin re-opening the same day sees the real counts, not a blank form.
const fetchAssignmentsAndLessons = async () => {
  if (!teacherId.value || !date.value) {
    rows.value = [];
    return;
  }

  isLoading.value = true;
  sharedNotes.value = '';

  try {
    const assignmentsRes = await api.get('/class-group-teachers', {
      params: { teacher_id: teacherId.value, per_page: 1000 },
    });
    const assignments = assignmentsRes.data.data || assignmentsRes.data || [];

    const newRows = assignments.map((a) => ({
      assignment_id: a.id,
      class_name: a.class?.name || '—',
      group_name: a.group?.name || '—',
      subject_name: a.subject?.name || '—',
      lesson_price: Number(a.lesson_price) || 0,
      // 0 until we know otherwise — overlaid with the real count just below
      // if lessons are already recorded for this teacher/date. Defaulting
      // to 1 used to make a subject nobody taught yet look pre-filled.
      count: 0,
      existing_ids: [],
    }));

    try {
      const existingRes = await api.get('/teacher-lessons', {
        params: { teacher_id: teacherId.value, date: date.value, per_page: 1000 },
      });
      const existingLessons = existingRes.data.data || [];

      const byAssignment = {};
      existingLessons.forEach((l) => {
        if (!byAssignment[l.class_group_teacher_id]) byAssignment[l.class_group_teacher_id] = [];
        byAssignment[l.class_group_teacher_id].push(l.id);
        if (l.notes) sharedNotes.value = l.notes;
      });

      newRows.forEach((row) => {
        const ids = byAssignment[row.assignment_id];
        if (ids && ids.length > 0) {
          row.existing_ids = ids;
          row.count = ids.length;
        }
      });
    } catch (e) {
      // No existing lessons for this teacher/date — that's fine, rows keep their default count of 1.
    }

    rows.value = newRows;
  } catch (error) {
    toast.error(t('teacherLessons.loadError'));
    rows.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Overlays which of the CURRENT rows already have lessons saved on the
// newly chosen date — unlike fetchAssignmentsAndLessons() this does NOT
// refetch or rebuild the assignment list, so counts the admin is still
// mid-typing for other subjects survive a date change instead of being
// wiped out by a full reload every time a date digit is typed.
const refreshExistingForDate = async () => {
  if (!teacherId.value || !date.value || rows.value.length === 0) return;

  isRefreshingDate.value = true;
  try {
    const existingRes = await api.get('/teacher-lessons', {
      params: { teacher_id: teacherId.value, date: date.value, per_page: 1000 },
    });
    const existingLessons = existingRes.data.data || [];

    const byAssignment = {};
    let notesForDate = '';
    existingLessons.forEach((l) => {
      if (!byAssignment[l.class_group_teacher_id]) byAssignment[l.class_group_teacher_id] = [];
      byAssignment[l.class_group_teacher_id].push(l.id);
      if (l.notes) notesForDate = l.notes;
    });

    rows.value.forEach((row) => {
      const ids = byAssignment[row.assignment_id];
      if (ids && ids.length > 0) {
        row.existing_ids = ids;
        row.count = ids.length;
      } else {
        // Nothing saved for this subject on the new date — clear the old
        // date's existing_ids so saveLessons() doesn't try to update rows
        // that belong to a different day, AND reset the count to 0. Simply
        // leaving whatever count was showing for the previous date meant a
        // brand-new/empty date displayed the old date's totals as if they
        // already applied here too.
        row.existing_ids = [];
        row.count = 0;
      }
    });

    sharedNotes.value = notesForDate;
  } catch (e) {
    // No existing lessons for this teacher/date — that's fine, rows keep their current counts.
  } finally {
    isRefreshingDate.value = false;
  }
};

// Removing a subject means "he didn't teach this today" — any lessons
// already saved for it on this date must be deleted right away, not just
// hidden from the form, otherwise they'd keep counting toward salary.
const removeRow = async (idx) => {
  const row = rows.value[idx];

  if (row.existing_ids.length > 0) {
    try {
      await Promise.all(row.existing_ids.map((id) => api.delete(`/teacher-lessons/${id}`)));
    } catch (e) {
      toast.error(t('common.error'));
      return;
    }
  }

  rows.value.splice(idx, 1);
};

const saveLessons = async () => {
  if (!teacherId.value || !date.value || rows.value.length === 0) return;

  isSaving.value = true;
  try {
    const lessonsPayload = [];
    const idsToDelete = [];

    rows.value.forEach((row) => {
      const count = Math.max(0, Number(row.count) || 0);
      const existing = row.existing_ids;

      // Reuse as many already-saved session rows as the new count still
      // needs — updating them keeps their notes/status intact server-side —
      // and only create new ones or delete extras for the difference.
      const keep = existing.slice(0, count);
      const toRemove = existing.slice(count);
      idsToDelete.push(...toRemove);

      keep.forEach((id) => {
        lessonsPayload.push({
          id,
          class_group_teacher_id: row.assignment_id,
          status: 'attended',
          notes: sharedNotes.value,
        });
      });

      for (let i = keep.length; i < count; i++) {
        lessonsPayload.push({
          id: null,
          class_group_teacher_id: row.assignment_id,
          status: 'attended',
          notes: sharedNotes.value,
        });
      }
    });

    if (idsToDelete.length > 0) {
      await Promise.all(idsToDelete.map((id) => api.delete(`/teacher-lessons/${id}`)));
    }

    if (lessonsPayload.length > 0) {
      const response = await api.post('/teacher-lessons', {
        lesson_date: date.value,
        lessons: lessonsPayload,
      });
      toast.success(response.data.message || t('teacherLessons.saved'));
    } else {
      toast.success(t('teacherLessons.saved'));
    }

    router.push({ name: 'teacher-lessons.index' });
  } catch (error) {
    toast.error(error.response?.data?.message || t('teacherLessons.saveError'));
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  if (!isSaving.value) router.push({ name: 'teacher-lessons.index' });
};

// Switching teacher needs a full reload (a different assignment list
// entirely). Switching the date only needs to re-check which of the
// already-loaded rows have saved lessons on that day — see
// refreshExistingForDate()'s comment for why a full reload there used to
// wipe in-progress counts every time the date field was edited.
watch(teacherId, () => fetchAssignmentsAndLessons());
watch(date, () => refreshExistingForDate());

onMounted(() => fetchTeachers());
</script>
